import { useEffect, useState } from 'react';
import { fetchPersonalStats } from '../lib/api';
import { TrendingUp, Flame, CheckCircle2, Target, Calendar } from 'lucide-react';

/**
 * PaginaStats — pagina /stats con metriche personali.
 *
 * Le 5 metriche, mostrate come "carte numero grande":
 *  1. MIT chiuse questo mese (col totale del mese in basso)
 *  2. % KR on-track (con frazione XX / YY KR attivi)
 *  3. Streak settimane consecutive con ≥1 MIT chiusa
 *  4. Miglior settimana (label + conteggio)
 *  5. MIT chiuse da inizio anno
 *
 * Tutto è read-only: nessuna action. Skeleton durante il fetch.
 */
export default function PaginaStats() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPersonalStats()
      .then(setStats)
      .catch((e) => {
        setError(e?.response?.data?.detail || 'Errore caricamento statistiche');
      });
  }, []);

  if (error) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-4">Statistiche personali</h1>
        <div className="border border-risk/20 bg-risk/10 text-risk p-4 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="stats-loading">
        <h1 className="font-display text-2xl md:text-3xl tracking-tight mb-2">Statistiche personali</h1>
        <p className="text-sm text-soft mb-8">Carico le tue metriche…</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`stats-skel-${i}`} className="border border-linesoft bg-canvas h-40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const { mit, kr, best_week, streak_weeks, anno } = stats;
  const mese = monthName(stats.mese);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="stats-page">
      <header className="mb-10">
        <h1 className="font-display text-2xl md:text-3xl tracking-tight">Statistiche personali</h1>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute mt-1">
          {mese} {anno} · aggiornato in tempo reale
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* MIT closed this month */}
        <StatCard
          icon={<CheckCircle2 size={18} strokeWidth={1.5} />}
          label="MIT chiuse"
          sub={`a ${mese}`}
          value={mit.closed_this_month}
          accent="emerald"
          testid="stat-mit-month"
          footer={mit.total_this_month > 0 ? `${mit.closed_this_month}/${mit.total_this_month} del mese` : null}
        />

        {/* % KR on-track */}
        <StatCard
          icon={<Target size={18} strokeWidth={1.5} />}
          label="KR on-track"
          sub={`≥ ${(kr.threshold * 100).toFixed(0)}% confidence`}
          value={kr.on_track_pct != null ? `${kr.on_track_pct}%` : '—'}
          accent={kr.on_track_pct == null ? 'gray' : kr.on_track_pct >= 70 ? 'emerald' : kr.on_track_pct >= 40 ? 'amber' : 'red'}
          testid="stat-kr-ontrack"
          footer={kr.total_active > 0 ? `${kr.on_track} su ${kr.total_active} attivi` : 'nessun KR attivo'}
        />

        {/* Streak */}
        <StatCard
          icon={<Flame size={18} strokeWidth={1.5} />}
          label="Streak settimane"
          sub="consecutive con ≥1 MIT"
          value={streak_weeks}
          accent={streak_weeks >= 4 ? 'emerald' : streak_weeks >= 1 ? 'amber' : 'gray'}
          testid="stat-streak"
          footer={streak_weeks > 0 ? `non interrotto da ${streak_weeks} ${streak_weeks === 1 ? 'settimana' : 'settimane'}` : 'inizia chiudendo 1 MIT'}
        />

        {/* Best week */}
        <StatCard
          icon={<TrendingUp size={18} strokeWidth={1.5} />}
          label="Miglior settimana"
          sub={best_week ? best_week.settimana : `${anno}`}
          value={best_week ? best_week.conteggio : 0}
          accent="ink"
          testid="stat-best-week"
          footer={best_week ? `${best_week.conteggio} MIT chiuse in ${best_week.settimana}` : 'ancora nessuna chiusura'}
        />

        {/* Closed this year */}
        <StatCard
          icon={<Calendar size={18} strokeWidth={1.5} />}
          label="MIT chiuse"
          sub={`tutto ${anno}`}
          value={mit.closed_this_year}
          accent="ink"
          testid="stat-mit-year"
          footer={stats.weeks_with_closure_count > 0 ? `in ${stats.weeks_with_closure_count} ${stats.weeks_with_closure_count === 1 ? 'settimana' : 'settimane'} diverse` : null}
        />
      </div>

      <footer className="mt-12 text-sm text-mute font-mono">
        <p>
          Le statistiche sono calcolate solo sui tuoi dati e si aggiornano automaticamente
          dopo ogni MIT chiusa.
        </p>
      </footer>
    </div>
  );
}

function StatCard({ icon, label, sub, value, accent = 'ink', testid, footer }) {
  const accents = {
    ink: 'border-line text-ink',
    emerald: 'border-ok/20 text-ok',
    amber: 'border-amber-200 text-amber-700',
    red: 'border-risk/20 text-risk',
    gray: 'border-line text-mute',
  };
  const accentBg = {
    ink: 'bg-paper',
    emerald: 'bg-ok/10',
    amber: 'bg-amber-50/60',
    red: 'bg-risk/10',
    gray: 'bg-canvas',
  };
  return (
    <div
      className={`border ${accents[accent]} ${accentBg[accent]} p-5 transition-colors`}
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-mute">{icon}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-soft">{label}</span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute">{sub}</span>
      </div>
      <div className="font-display text-4xl md:text-5xl tabular-nums tracking-tight" data-testid={`${testid}-value`}>
        {value}
      </div>
      {footer && (
        <div className="mt-3 font-mono text-[11px] text-soft" data-testid={`${testid}-footer`}>
          {footer}
        </div>
      )}
    </div>
  );
}

function monthName(m) {
  const names = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
  return names[(m || 1) - 1];
}
